"use client";

import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Chip,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Grid,
  Link,
  CircularProgress,
} from "@mui/material";
import { MoreVert as MoreVertIcon, Add as AddIcon } from "@mui/icons-material";
import { MoveRight } from "lucide-react";
import SplitLegModal from "src/components/apps/LegsComponents/SplitLegModal";
import AddEquipmentModal from "src/components/apps/LegsComponents/AddEquipmentModal"; // Import AddEquipmentModal
import AddToExistingManifestModal from "src/components/apps/LegsComponents/AddToExistingManifestModal"; // Import AddToExistingManifestModal
import AssignDriverCarrier from "src/components/apps/order-components/legs/AssignDriverCarrier"; // Import AssignDriverCarrier
import EditAllocatedRevenue from "src/components/apps/order-components/legs/EditAllocatedRevenue";
import ChangeTimesModal from "src/components/apps/LegsComponents/ChangeTimesModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  splitLeg,
  deleteLeg,
  assignCarrierToLeg,
  assignCarrierWithLegToManifest,
  assignEquipmentToLeg,
  updateLegIsLineHaul,
  addLegToManifest,
  removeLegFromManifest,
  completeLeg,
} from "src/queries/query";
import { allocateRevenueAcrossLegs } from "src/queries/queriess";
import { OrderStatus } from "src/constants/OrderStatus";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const LegsTable = ({
  order,
  selectedQuote,
  onOpenCreateManifestModal,
  onOpenAddEquipmentModal,
  onOpenAddToManifestModal,
  onOpenSplitLegModal,
  formatDate,
  refetchOrders,
  onMarkAsPickedUp,
  onMarkAsDelivered,
  isOSND = false,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSplitLegModalOpen, setIsSplitLegModalOpen] = useState(false);
  const [selectedLegToSplit, setSelectedLegToSplit] = useState(null);
  const [isAssignCarrierModalOpen, setIsAssignCarrierModalOpen] =
    useState(false);
  const [selectedLegForAssignment, setSelectedLegForAssignment] =
    useState(null);
  const [isAddEquipmentModalOpen, setIsAddEquipmentModalOpen] = useState(false); // New state for AddEquipmentModal
  const [selectedLegForEquipment, setSelectedLegForEquipment] = useState(null); // New state for leg to assign equipment to
  const [
    isAddToExistingManifestModalOpen,
    setIsAddToExistingManifestModalOpen,
  ] = useState(false); // New state for AddToExistingManifestModal
  const [selectedLegForManifest, setSelectedLegForManifest] = useState(null); // New state for leg to add to manifest
  const [isAllocatedRevenueModalOpen, setIsAllocatedRevenueModalOpen] =
    useState(false);
  const [isChangeTimesModalOpen, setIsChangeTimesModalOpen] = useState(false);
  const [selectedLegForTimes, setSelectedLegForTimes] = useState(null);
  const [pendingLineHaulLegUuid, setPendingLineHaulLegUuid] = useState(null);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  // Placeholder for marking leg as picked up
  const handleMarkAsPickedUp = (leg) => {
    // Implement mutation here to update leg status to 'Picked Up'
    if (onMarkAsDelivered && leg?.uuid) {
      onMarkAsDelivered(leg);
    }
  };

  // Handler for marking leg as delivered
  const handleMarkAsDelivered = (leg) => {
    if (onMarkAsDelivered && leg?.uuid) {
      onMarkAsDelivered(leg);
    }
  };

  const addLegToManifestMutation = useMutation({
    mutationFn: ({ manifestUuid, legUuid }) =>
      addLegToManifest(manifestUuid, legUuid),
    onSuccess: async () => {
      toast.success("Leg added to manifest successfully!");
      setIsAddToExistingManifestModalOpen(false);
      setSelectedLegForManifest(null);
      queryClient.invalidateQueries(["orders"]);
      if (refetchOrders) {
        refetchOrders();
      }

      // Update order status to "in-transit" when leg is added to manifest
      if (selectedLegForManifest?.orderStop?.order?.uuid) {
        try {
          await updateOrderStatus(
            selectedLegForManifest.orderStop.order.uuid,
            OrderStatus.INTRANSIT,
          );
        } catch (error) {
          console.error("Failed to update order status:", error);
        }
      }
    },
    onError: (error) => {
      console.error("Failed to add leg to manifest:", error);
      toast.error(`Failed to add leg to manifest: ${error.message}`);
    },
  });

  const assignEquipmentMutation = useMutation({
    mutationFn: ({ legUuid, equipmentUuids }) =>
      assignEquipmentToLeg(legUuid, equipmentUuids),
    onSuccess: () => {
      toast.success("Equipment assigned to leg successfully!");
      setIsAddEquipmentModalOpen(false);
      setSelectedLegForEquipment(null);
      queryClient.invalidateQueries(["orders"]);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error("Failed to assign equipment to leg:", error);
      toast.error(`Failed to assign equipment to leg: ${error.message}`);
    },
  });

  const splitLegMutation = useMutation({
    mutationFn: ({ legUuid, payload }) => splitLeg(legUuid, payload),
    onSuccess: () => {
      toast.success("Leg split successfully!");
      setIsSplitLegModalOpen(false);
      setSelectedLegToSplit(null);
      queryClient.invalidateQueries(["orders"]);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error("Failed to split leg:", error);
      toast.error(`Failed to split leg: ${error.message}`);
    },
  });

  const lineHaulMutation = useMutation({
    // Accept an object so we can toggle on/off
    mutationFn: ({ legUuid, isLineHaul }) => {
      setPendingLineHaulLegUuid(legUuid);
      return updateLegIsLineHaul(legUuid, isLineHaul);
    },
    onMutate: async ({ legUuid, isLineHaul }) => {
      // Optimistically update the cache
      if (order?.uuid) {
        // Update the order in cache optimistically
        queryClient.setQueryData(["orders", order.uuid], (oldData) => {
          if (!oldData) return oldData;

          const updateLegInOrder = (orderData) => {
            if (orderData.orderStops) {
              return {
                ...orderData,
                orderStops: orderData.orderStops.map((stop) => ({
                  ...stop,
                  legs: stop.legs?.map((leg) =>
                    leg.uuid === legUuid
                      ? {
                          ...leg,
                          isLineHaul,
                          isLinehaul: isLineHaul,
                          lineHaul: isLineHaul,
                          linehaul: isLineHaul,
                          is_line_haul: isLineHaul,
                          line_haul: isLineHaul,
                        }
                      : leg,
                  ),
                })),
              };
            } else if (orderData.legs) {
              return {
                ...orderData,
                legs: orderData.legs.map((leg) =>
                  leg.uuid === legUuid
                    ? {
                        ...leg,
                        isLineHaul,
                        isLinehaul: isLineHaul,
                        lineHaul: isLineHaul,
                        linehaul: isLineHaul,
                        is_line_haul: isLineHaul,
                        line_haul: isLineHaul,
                      }
                    : leg,
                ),
              };
            }
            return orderData;
          };

          return updateLegInOrder(oldData);
        });
      }
    },
    onSuccess: (_data, variables) => {
      if (variables?.isLineHaul) {
        toast.success("Leg marked as Line Haul.");
      } else {
        toast.success("Line Haul removed from leg.");
      }
      // Invalidate only the specific order query in background - optimistic update already handled UI
      if (order?.uuid) {
        queryClient.invalidateQueries(["orders", order.uuid], { exact: true });
      }
      setPendingLineHaulLegUuid(null);
    },
    onError: (error, variables, context) => {
      console.error("Failed to update Line Haul status:", error);
      toast.error(`Failed to update Line Haul status: ${error.message}`);
      // Rollback optimistic update on error
      if (order?.uuid) {
        queryClient.invalidateQueries(["orders", order.uuid]);
      }
      setPendingLineHaulLegUuid(null);
    },
  });

  const deleteLegMutation = useMutation({
    mutationFn: (legUuid) => deleteLeg([legUuid]),
    onSuccess: () => {
      toast.success("Leg deleted successfully!");
      queryClient.invalidateQueries(["orders"]);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error("Failed to delete leg:", error);
      toast.error(`Failed to delete leg: ${error.message}`);
    },
  });

  const removeLegFromManifestMutation = useMutation({
    mutationFn: async ({ manifestUuid, legUuid }) => {
      console.log("Removing leg from manifest:", { manifestUuid, legUuid });
      const result = await removeLegFromManifest(manifestUuid, legUuid);
      console.log("Remove leg response:", result);
      return result;
    },
    onSuccess: async (data, variables) => {
      console.log("Successfully removed leg from manifest");
      toast.success("Leg removed from manifest successfully!");

      // Find the leg to get order UUID
      let leg = null;
      if (order?.orderStops) {
        leg = order.orderStops
          .flatMap((stop) => stop.legs || [])
          .find((l) => l.uuid === variables.legUuid);
      } else if (order?.legs) {
        leg = order.legs.find((l) => l.uuid === variables.legUuid);
      }
      console.log("Leg found for status update:", leg);

      // Update order status back to "booked" when leg is removed from manifest
      if (leg?.orderStop?.order?.uuid) {
        try {
          console.log(
            "Updating order status to BOOKED for:",
            leg.orderStop.order.uuid,
          );
          await updateOrderStatus(leg.orderStop.order.uuid, OrderStatus.BOOKED);
          console.log("Order status updated successfully");
        } catch (error) {
          console.error("Failed to update order status:", error);
        }
      } else {
        console.error("Could not find order UUID for leg:", variables.legUuid);
      }

      // Refetch after status update
      console.log("Invalidating queries and refetching...");
      queryClient.invalidateQueries(["orders"]);
      queryClient.invalidateQueries(["manifests"]);
      queryClient.invalidateQueries(["order"]);
      if (refetchOrders) {
        await refetchOrders();
      }
      console.log("Refetch completed");
    },
    onError: (error) => {
      console.error("Failed to remove leg from manifest:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
      });
      toast.error(`Failed to remove leg from manifest: ${error.message}`);
    },
  });

  const saveAllocatedRevenueMutation = useMutation({
    mutationFn: async (payload) => {
      const updates = Array.isArray(payload) ? payload : [];
      if (!order?.uuid) {
        throw new Error("Missing order uuid");
      }

      const allocations = updates
        .filter((u) => u?.uuid)
        .map((u) => ({ legUuid: u.uuid, revenue: u.revenue }));

      return allocateRevenueAcrossLegs({
        orderUuid: order.uuid,
        totalRevenue: allocatedRevenueTotal,
        allocations,
      });
    },
    onSuccess: () => {
      toast.success("Allocated revenue updated successfully!");
      setIsAllocatedRevenueModalOpen(false);
      queryClient.invalidateQueries(["orders"]);
      if (refetchOrders) {
        refetchOrders();
      }
    },
    onError: (error) => {
      console.error("Failed to update allocated revenue:", error);
      toast.error(`Failed to update allocated revenue: ${error.message}`);
    },
  });

  const isLegLineHaul = (leg) => {
    // Check all possible field names (camelCase, lowercase, snake_case)
    // and handle various truthy values (boolean, string "true", number 1)
    const value =
      leg?.isLineHaul ??
      leg?.isLinehaul ??
      leg?.lineHaul ??
      leg?.linehaul ??
      leg?.is_line_haul ??
      leg?.line_haul;
    if (value === true || value === "true" || value === 1 || value === "1") {
      return true;
    }
    return !!value;
  };

  const handleOpenSplitLegModal = (leg) => {
    setSelectedLegToSplit(leg);
    setIsSplitLegModalOpen(true);
  };

  const handleCloseSplitLegModal = () => {
    setIsSplitLegModalOpen(false);
    setSelectedLegToSplit(null);
  };

  const handleSplitLegSubmit = (addressData) => {
    if (!selectedLegToSplit) {
      console.error("No leg selected for splitting.");
      return;
    }

    const payload = {
      origin: {
        address: addressData.address,
        address2: addressData.address2 || null,
        city: addressData.city,
        state: addressData.state,
        postal: addressData.postal,
        country: addressData.country,
        name: addressData.newCompanyName,
        latitude: Number.parseFloat(addressData.latitude),
        longitude: Number.parseFloat(addressData.longitude),
      },
      revenue: selectedLegToSplit.revenue || 0,
      costs: selectedLegToSplit.costs || 0,
      margin: selectedLegToSplit.margin || 0,
      newCompanyName: addressData.newCompanyName,
    };

    splitLegMutation.mutate({ legUuid: selectedLegToSplit.uuid, payload });
  };

  const sortedLegs = useMemo(() => {
    if (!order) return [];

    let legs = [];
    if (order.orderStops) {
      legs = order.orderStops.flatMap((stop) => stop.legs || []);
    } else if (order.legs) {
      legs = order.legs;
    }

    const parseLegNumberSegments = (legNumber) => {
      if (legNumber === null || legNumber === undefined) return null;

      const s = String(legNumber);
      const legMatch = s.match(/LEG-([0-9]+(?:\.[0-9]+)*)/i);
      const tokenFromLeg = legMatch?.[1];

      const allTokens = s.match(/\d+(?:\.\d+)*/g);
      const tokenFallback = Array.isArray(allTokens)
        ? allTokens[allTokens.length - 1]
        : null;

      const token = tokenFromLeg || tokenFallback;
      if (!token) return null;

      const parts = token.split(".").filter(Boolean);
      if (!parts.length) return null;

      const segments = parts
        .map((p) => Number.parseInt(p, 10))
        .filter((n) => Number.isFinite(n));

      return segments.length ? segments : null;
    };

    const compareSegments = (aSeg, bSeg) => {
      if (!aSeg && !bSeg) return 0;
      if (!aSeg) return 1;
      if (!bSeg) return -1;

      const maxLen = Math.max(aSeg.length, bSeg.length);
      for (let i = 0; i < maxLen; i += 1) {
        const aVal = aSeg[i] ?? 0;
        const bVal = bSeg[i] ?? 0;
        if (aVal !== bVal) return aVal - bVal;
      }
      return 0;
    };

    const filteredLegs = legs
      .filter((leg) => leg)
      .sort((a, b) => {
        const segA = parseLegNumberSegments(a?.legNumber);
        const segB = parseLegNumberSegments(b?.legNumber);
        return compareSegments(segA, segB);
      });
    return filteredLegs;
  }, [order]);

  const handleMenuOpen = (event, rowId) => {
    setAnchorEl(event.currentTarget);
    setSelectedRow(rowId);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  const totals = useMemo(() => {
    const total = Number.parseFloat(selectedQuote?.quoteData?.total || 0);
    const carrierCosts = Number.parseFloat(selectedQuote?.carrierCosts || 0);
    const discountPercent = Number.parseFloat(
      selectedQuote?.totalDiscount || 0,
    );
    const marginBeforeDiscount = total - carrierCosts;
    const discountAmount =
      selectedQuote?.discountType === "$"
        ? selectedQuote?.totalDiscount || 0
        : marginBeforeDiscount * (discountPercent / 100);
    const finalMargin = marginBeforeDiscount - discountAmount;

    return {
      revenue: total,
      costs: carrierCosts,
      discount: discountAmount,
      margin: finalMargin,
      isNegativeMargin: finalMargin < 0,
    };
  }, [selectedQuote]);

  const allocatedRevenueTotal = useMemo(() => {
    if (totals?.revenue > 0) return totals.revenue;
    return sortedLegs.reduce(
      (acc, leg) => acc + (Number(leg?.revenue) || 0),
      0,
    );
  }, [sortedLegs, totals?.revenue]);

  const shouldShowDeleteLeg = (legs, index) => {
    const totalLegs = Array.isArray(legs) ? legs.filter(Boolean).length : 0;
    return totalLegs > 1 && index > 0;
  };

  const handleDeleteLeg = (leg, index) => {
    if (!leg?.uuid) return;
    if (index <= 0) return;
    deleteLegMutation.mutate(leg.uuid);
  };

  const LegActions = ({ leg, index, onMarkAsPickedUp, onMarkAsDelivered }) => {
    // Check if leg has a manifest assigned (check both possible paths)
    const manifest = leg?.manifest || leg?.manifestStop?.manifest;
    const hasManifest = !!(
      manifest?.manifestName ||
      manifest?.manifestNumber ||
      manifest?.uuid ||
      leg?.manifestId
    );

    return (
      <Box
        sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}
      >
        {/* When leg HAS manifest: Show View Manifest, hide Assign/Add buttons */}
        {hasManifest ? (
          <>
            <Button
              variant="contained"
              size="small"
              color="primary"
              onClick={() =>
                navigate(
                  `/manifest/blank/${manifest?.uuid || leg?.manifestUuid}`,
                )
              }
            >
              View Manifest
            </Button>
            {leg.type === "DELIVERY" &&
              leg.status?.toLowerCase() !== "delivered" && (
                <Button
                  size="small"
                  color="primary"
                  onClick={() => onMarkAsDelivered?.(leg)}
                >
                  Mark as Delivered
                </Button>
              )}
            <Button
              size="small"
              color="primary"
              onClick={() => handleOpenSplitLegModal?.(leg)}
            >
              Split Leg
            </Button>
            <Button
              size="small"
              color={isLegLineHaul(leg) ? "inherit" : "secondary"}
              variant={isLegLineHaul(leg) ? "outlined" : "contained"}
              disabled={pendingLineHaulLegUuid === leg?.uuid}
              onClick={() => {
                if (leg?.uuid) {
                  lineHaulMutation.mutate({
                    legUuid: leg.uuid,
                    isLineHaul: !isLegLineHaul(leg),
                  });
                }
              }}
              startIcon={
                pendingLineHaulLegUuid === leg?.uuid ? (
                  <CircularProgress size={16} />
                ) : null
              }
            >
              {isLegLineHaul(leg) ? "Unset Line Haul" : "Set as Line Haul"}
            </Button>
          </>
        ) : (
          <>
            {/* When leg does NOT have manifest: Show Assign/Add buttons */}
            <Button
              size="small"
              color="primary"
              onClick={() => {
                setSelectedLegForAssignment?.(leg);
                setIsAssignCarrierModalOpen(true);
                handleMenuClose();
              }}
            >
              Assign carrier/driver
            </Button>
            <Button
              size="small"
              color="primary"
              onClick={() => {
                setSelectedLegForManifest(leg);
                setIsAddToExistingManifestModalOpen(true);
              }}
            >
              Add To Existing Manifest
            </Button>
            <Button
              size="small"
              color="primary"
              onClick={() => handleOpenSplitLegModal?.(leg)}
            >
              Split Leg
            </Button>
            <Button
              size="small"
              color={isLegLineHaul(leg) ? "inherit" : "secondary"}
              variant={isLegLineHaul(leg) ? "outlined" : "contained"}
              disabled={pendingLineHaulLegUuid === leg?.uuid}
              onClick={() => {
                if (leg?.uuid) {
                  lineHaulMutation.mutate({
                    legUuid: leg.uuid,
                    isLineHaul: !isLegLineHaul(leg),
                  });
                }
              }}
              startIcon={
                pendingLineHaulLegUuid === leg?.uuid ? (
                  <CircularProgress size={16} />
                ) : null
              }
            >
              {isLegLineHaul(leg) ? "Unset Line Haul" : "Set as Line Haul"}
            </Button>
          </>
        )}

        {/* Delete Leg button - always available when applicable */}
        {shouldShowDeleteLeg(sortedLegs, index) && (
          <Button
            size="small"
            color="error"
            onClick={() => {
              handleDeleteLeg(leg, index);
            }}
          >
            Delete Leg
          </Button>
        )}
      </Box>
    );
  };

  const LegRow = ({
    leg,
    index,
    setSelectedLegForAssignment,
    setIsAssignCarrierModalOpen,
    handleMenuClose,
    onMarkAsPickedUp,
    onMarkAsDelivered,
  }) => {
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const handleClick = (event) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
      setAnchorEl(null);
    };
    const ITEM_HEIGHT = 48;
    return (
      <React.Fragment key={leg?.uuid || index}>
        <TableRow>
          <TableCell>
            <Chip
              label={leg?.status || "N/A"}
              size="small"
              sx={{ bgcolor: "#e0e0e0" }}
            />
          </TableCell>
          <TableCell>
            {/* <Typography variant="body2">{leg?.type || 'N/A'}</Typography> */}
            <div className="font-12 font-bold flex items-center gap-1">
              P/D
              <svg
                width="12"
                height="12"
                viewBox="0 0 13 18"
                xmlns="http://www.w3.org/2000/svg"
              >
                <title>compare_arrows - material</title>
                <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                  <g transform="translate(-37 -4)" fill="#000">
                    <path
                      d="M46.1546392,13.8453608 L42.7319588,10.4226804 L46.1546392,7 L46.1546392,9.57731959 L52.1546392,9.57731959 L52.1546392,11.2680412 L46.1546392,11.2680412 L46.1546392,13.8453608 Z M41,14.7319588 L41,12.1546392 L44.4226804,15.5773196 L41,19 L41,16.4226804 L35,16.4226804 L35,14.7319588 L41,14.7319588 Z"
                      transform="translate(43.57732 13) scale(1 -1) rotate(-90) translate(-43.57732 -13)"
                    />
                  </g>
                </g>
              </svg>
            </div>

            {isLegLineHaul(leg) && (
              <Chip
                label="Line Haul"
                size="small"
                color="secondary"
                sx={{ mt: 0.5 }}
              />
            )}
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {leg?.origin?.name ||
                leg?.origin?.companyName ||
                leg?.destination?.companyName ||
                "N/A"}
            </Typography>
            <Typography variant="body2">
              {leg?.origin?.address || "N/A"}
            </Typography>
            <Typography variant="caption">
              {formatDate?.(leg?.readyTimeEnd) || "N/A"}
            </Typography>
          </TableCell>
          <TableCell>
            <MoveRight fontSize="small" sx={{ color: "primary.main" }} />
          </TableCell>
          <TableCell>
            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {leg?.destination?.name || "N/A"}
            </Typography>
            <Typography variant="body2">
              {leg?.destination?.address || "N/A"}
            </Typography>
            <Typography variant="caption">
              {formatDate?.(leg?.requestedTimeEnd) || "N/A"}
            </Typography>
          </TableCell>
          <TableCell>
            {(() => {
              const legManifest = leg?.manifest || leg?.manifestStop?.manifest;
              const manifestUuid = legManifest?.uuid || leg?.manifestUuid;
              const manifestName =
                legManifest?.manifestName ||
                leg?.manifestName ||
                legManifest?.manifestNumber ||
                leg?.manifestNumber;

              if (manifestUuid || manifestName) {
                return (
                  <Link
                    href={`/manifest/blank/${manifestUuid}`}
                    sx={{
                      color: "#0F3B95",
                      fontWeight: "bold",
                      textDecoration: "none",
                      "&:hover": { textDecoration: "underline" },
                    }}
                  >
                    {manifestName || "Manifest"}
                  </Link>
                );
              }
              return (
                <Typography variant="body2" color="text.secondary">
                  —
                </Typography>
              );
            })()}
          </TableCell>
          <TableCell>
            <Typography
              variant="body2"
              color="error.main"
              sx={{ fontWeight: "bold", mb: 0.5 }}
            >
              - $ {leg?.costs?.toFixed(2) || "0.00"}
            </Typography>
            <Typography
              sx={{ fontWeight: "bold" }}
              variant="body2"
              color="success.main"
            >
              + $ {leg?.margin?.toFixed(2) || "0.00"}
            </Typography>
          </TableCell>
          <TableCell sx={{ position: "relative" }}>
            <IconButton
              aria-label="more"
              id="long-button"
              aria-controls={open ? "long-menu" : undefined}
              aria-expanded={open ? "true" : undefined}
              aria-haspopup="true"
              onClick={handleClick}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="long-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              slotProps={{
                paper: {
                  style: {
                    maxHeight: ITEM_HEIGHT * 4.5,
                    width: "20ch",
                  },
                },
                list: {
                  "aria-labelledby": "long-button",
                },
              }}
            >
              {/* Check if leg has a manifest */}
              {(() => {
                const legManifest =
                  leg?.manifest || leg?.manifestStop?.manifest;
                const manifestUuid = legManifest?.uuid || leg?.manifestUuid;
                const hasManifest = !!(
                  legManifest?.manifestName ||
                  legManifest?.manifestNumber ||
                  manifestUuid ||
                  leg?.manifestId
                );
                return (
                  <>
                    {/* Show View Manifest when leg has a manifest */}
                    {hasManifest && (
                      <MenuItem
                        onClick={() => {
                          navigate(`/manifest/blank/${manifestUuid}`);
                          handleMenuClose();
                        }}
                      >
                        View Manifest
                      </MenuItem>
                    )}

                    {/* Hide Assign Carrier/Driver when leg has a manifest */}
                    {!hasManifest && (
                      <MenuItem
                        onClick={() => {
                          setSelectedLegForAssignment(leg);
                          setIsAssignCarrierModalOpen(true);
                          handleMenuClose();
                        }}
                      >
                        Assign Carrier/Driver
                      </MenuItem>
                    )}

                    {/* Hide Add To Existing Manifest when leg has a manifest */}
                    {!hasManifest && (
                      <MenuItem
                        onClick={() => {
                          onOpenAddToManifestModal?.(leg);
                          setSelectedLegForManifest(leg);
                          setIsAddToExistingManifestModalOpen(true);
                          handleMenuClose();
                        }}
                      >
                        Add To Existing Manifest
                      </MenuItem>
                    )}

                    {/* Show Remove from Manifest when leg has a manifest */}
                    {hasManifest && (
                      <MenuItem
                        onClick={() => {
                          setSelectedLegForManifest(leg);
                          removeLegFromManifestMutation.mutate({
                            manifestUuid: manifestUuid,
                            legUuid: leg.uuid,
                          });
                          handleMenuClose();
                        }}
                      >
                        Remove from Manifest
                      </MenuItem>
                    )}
                  </>
                );
              })()}
              <MenuItem
                onClick={() => {
                  handleOpenSplitLegModal?.(leg);
                  handleMenuClose();
                }}
              >
                Split Leg
              </MenuItem>

              <MenuItem
                disabled={pendingLineHaulLegUuid === leg?.uuid}
                onClick={() => {
                  if (leg?.uuid) {
                    lineHaulMutation.mutate({
                      legUuid: leg.uuid,
                      isLineHaul: !isLegLineHaul(leg),
                    });
                  }
                  handleMenuClose();
                }}
              >
                {pendingLineHaulLegUuid === leg?.uuid ? (
                  <>
                    <CircularProgress size={16} sx={{ mr: 1 }} />
                    {isLegLineHaul(leg) ? "Unsetting..." : "Setting..."}
                  </>
                ) : isLegLineHaul(leg) ? (
                  "Unset Line Haul"
                ) : (
                  "Set as Line Haul"
                )}
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setSelectedLegForTimes(leg);
                  setIsChangeTimesModalOpen(true);
                  handleMenuClose();
                }}
              >
                Change Times
              </MenuItem>
              {shouldShowDeleteLeg(sortedLegs, index) && (
                <MenuItem
                  onClick={() => {
                    handleDeleteLeg(leg, index);
                    handleMenuClose();
                  }}
                >
                  Delete Leg
                </MenuItem>
              )}
            </Menu>
          </TableCell>
        </TableRow>

        <TableRow sx={{ backgroundColor: "#fff" }}>
          <TableCell colSpan={8} sx={{ py: 1 }}>
            <LegActions
              leg={leg}
              index={index}
              onMarkAsPickedUp={onMarkAsPickedUp}
              onMarkAsDelivered={onMarkAsDelivered}
            />
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  };

  const FinancialSummary = () => (
    <Grid
      container
      spacing={0}
      sx={{ mt: 2, width: "30%", marginLeft: "auto" }}
    >
      {totals.revenue > 0 && (
        <Grid item xs={12}>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Total Revenue (CAD)
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              $ {totals.revenue.toFixed(2)}
            </Typography>
          </Box>
        </Grid>
      )}

      {totals.costs > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
              color: "error.main",
            }}
          >
            <Typography variant="body2">Total Costs (CAD)</Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              - $ {totals.costs.toFixed(2)}
            </Typography>
          </Box>
        </Grid>
      )}

      {selectedQuote?.totalDiscount > 0 && (
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
              color: "error.main",
            }}
          >
            <Typography variant="body2">
              Total Discount {selectedQuote?.discountType}
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
              - {selectedQuote?.discountType}{" "}
              {selectedQuote?.totalDiscount?.toFixed(2)}
            </Typography>
          </Box>
        </Grid>
      )}
    </Grid>
  );

  return (
    <Box sx={{ px: "16px !important" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6">
          Legs {sortedLegs.length > 0 && `(${sortedLegs.length})`}
        </Typography>

        {sortedLegs.length > 0 && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsAllocatedRevenueModalOpen(true)}
          >
            Edit Allocated Revenue
          </Button>
        )}
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell>Status</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Origin</TableCell>
              <TableCell></TableCell>
              <TableCell>Destination</TableCell>
              <TableCell>Manifest</TableCell>
              <TableCell>Est. Margin</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedLegs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ py: 4, textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    No legs available for this order
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              sortedLegs.map((leg, index) => (
                <LegRow
                  key={leg?.uuid || `leg-${index}`}
                  leg={leg}
                  index={index}
                  setSelectedLegForAssignment={setSelectedLegForAssignment}
                  setIsAssignCarrierModalOpen={setIsAssignCarrierModalOpen}
                  handleMenuClose={handleMenuClose}
                  onMarkAsPickedUp={handleMarkAsPickedUp}
                  onMarkAsDelivered={handleMarkAsDelivered}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <FinancialSummary />

      <SplitLegModal
        open={isSplitLegModalOpen}
        handleClose={handleCloseSplitLegModal}
        handleSubmit={handleSplitLegSubmit}
      />
      <AssignDriverCarrier
        open={isAssignCarrierModalOpen}
        handleClose={() => setIsAssignCarrierModalOpen(false)}
        leg={selectedLegForAssignment}
        isOSND={isOSND}
      />
      <AddEquipmentModal
        open={isAddEquipmentModalOpen}
        handleClose={() => setIsAddEquipmentModalOpen(false)}
        leg={selectedLegForEquipment}
        onAssign={(legUuid, equipmentUuids) => {
          if (legUuid && equipmentUuids.length > 0) {
            assignEquipmentMutation.mutate({
              legUuid,
              equipmentUuids,
            });
          } else {
            toast.error("No leg or equipment selected for assignment.");
          }
        }}
      />
      <AddToExistingManifestModal
        open={isAddToExistingManifestModalOpen}
        handleClose={() => setIsAddToExistingManifestModalOpen(false)}
        // Modal expects an array of legs; pass the selected leg if present
        legs={selectedLegForManifest ? [selectedLegForManifest] : []}
        refetchOrders={refetchOrders}
        isOSND={isOSND}
      />

      <EditAllocatedRevenue
        data={{}}
        order={order}
        // Include all legs; EditAllocatedRevenue will internally treat Line Haul legs as fixed 0
        legs={sortedLegs}
        totalRevenue={allocatedRevenueTotal}
        orderUuid={order?.uuid}
        openAllocated={isAllocatedRevenueModalOpen}
        handleCloseAllocated={() => setIsAllocatedRevenueModalOpen(false)}
        onSave={(payload) => saveAllocatedRevenueMutation.mutateAsync(payload)}
      />
      <ChangeTimesModal
        open={isChangeTimesModalOpen}
        handleClose={() => {
          setIsChangeTimesModalOpen(false);
          setSelectedLegForTimes(null);
        }}
        leg={selectedLegForTimes}
        refetchOrders={refetchOrders}
      />
    </Box>
  );
};

export default LegsTable;
